/**
 * Painel de comentarios de uma fase.
 *
 * Backend devolve `pode_editar` calculado (autor dentro de janela de 15min
 * ou admin). Frontend so confia nesse flag — nao decide regra propria.
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Pencil, Trash2, MessageSquare } from 'lucide-react';
import { fasesAPI, comentariosAPI } from '../../../services/inovacaoAPI';
import { mensagemErro } from '../labels';

export default function ComentariosFase({ faseId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [textoEdicao, setTextoEdicao] = useState('');

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    fasesAPI.listarComentarios(faseId)
      .then((data) => { if (!cancelado) setItems(data); })
      .catch((e) => { if (!cancelado) toast.error(mensagemErro(e, 'Erro ao carregar comentarios')); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [faseId]);

  async function enviar(e) {
    e.preventDefault();
    const t = texto.trim();
    if (!t) return;
    setEnviando(true);
    try {
      const novo = await fasesAPI.criarComentario(faseId, t);
      setItems((arr) => [...arr, novo]);
      setTexto('');
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao enviar'));
    }
    setEnviando(false);
  }

  function iniciarEdicao(c) {
    setEditandoId(c.id);
    setTextoEdicao(c.texto);
  }

  async function salvarEdicao(c) {
    const t = textoEdicao.trim();
    if (!t) return;
    try {
      const atualizado = await comentariosAPI.atualizar(c.id, t);
      setItems((arr) => arr.map((x) => x.id === c.id ? atualizado : x));
      setEditandoId(null);
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao editar'));
    }
  }

  async function remover(c) {
    if (!window.confirm('Remover este comentario?')) return;
    try {
      await comentariosAPI.deletar(c.id);
      setItems((arr) => arr.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao remover'));
    }
  }

  return (
    <div>
      <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#2c3e50',
                   display: 'flex', alignItems: 'center', gap: 6 }}>
        <MessageSquare size={14} /> Comentarios ({items.length})
      </h4>

      {loading ? (
        <p style={textoMuted}>Carregando…</p>
      ) : items.length === 0 ? (
        <p style={textoMuted}>Nenhum comentario. Seja o primeiro.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', fontSize: '0.85rem',
                     maxHeight: 280, overflowY: 'auto' }}>
          {items.map((c) => (
            <li key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid #ecf0f1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontWeight: 500, color: '#2c3e50' }}>
                  {c.autor?.nome || 'Anonimo'}
                </span>
                <span style={{ fontSize: '0.72rem', color: '#95a5a6' }}>
                  {formatarData(c.criado_em)}
                  {c.editado_em && ' (editado)'}
                </span>
              </div>
              {editandoId === c.id ? (
                <div>
                  <textarea value={textoEdicao}
                            onChange={(e) => setTextoEdicao(e.target.value)}
                            style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                            maxLength={4000} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <button onClick={() => salvarEdicao(c)} style={btnPrimario}>Salvar</button>
                    <button onClick={() => setEditandoId(null)} style={btnSecundario}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ color: '#34495e', whiteSpace: 'pre-wrap' }}>{c.texto}</div>
                  {c.pode_editar && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                      <button onClick={() => iniciarEdicao(c)} style={btnAcao} title="Editar">
                        <Pencil size={11} />
                      </button>
                      <button onClick={() => remover(c)} style={{ ...btnAcao, color: '#e74c3c' }}
                              title="Remover">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={enviar}>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escreva um comentario…"
          maxLength={4000}
          style={{ ...inputStyle, width: '100%', minHeight: 60, resize: 'vertical' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginTop: 4 }}>
          <span style={{ fontSize: '0.7rem', color: '#95a5a6' }}>
            Voce pode editar/remover nos primeiros 15 min apos enviar.
          </span>
          <button type="submit"
                  disabled={enviando || !texto.trim()}
                  style={{ ...btnPrimario, opacity: (enviando || !texto.trim()) ? 0.6 : 1 }}>
            {enviando ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function formatarData(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const inputStyle = {
  padding: 8, borderRadius: 4, border: '1px solid #ddd',
  fontSize: '0.85rem', fontFamily: 'inherit', boxSizing: 'border-box',
};
const btnPrimario = {
  background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', padding: '6px 14px',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem',
};
const btnSecundario = {
  background: '#ecf0f1', color: '#555', padding: '6px 14px',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem',
};
const btnAcao = {
  background: 'transparent', border: 'none', color: '#7f8c8d',
  cursor: 'pointer', padding: '2px 4px', fontSize: '0.72rem',
  display: 'inline-flex', alignItems: 'center',
};
const textoMuted = { color: '#bdc3c7', fontSize: '0.85rem' };
